import { useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Maximize2,
  Loader2,
} from 'lucide-react'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import type { GeneratedImage, OutpaintImageRequest } from '../../lib/api'
import { getImageAssetUrl, outpaintImage } from '../../lib/api'
import { toast } from 'sonner'

interface OutpaintingPanelProps {
  images: GeneratedImage[]
  onSuccess?: () => void
}

type Direction = OutpaintImageRequest['direction']

const DIRECTION_CONFIG: {
  id: Direction
  label: string
  icon: React.ComponentType<{ className?: string }>
  gridArea: string
}[] = [
  { id: 'top', label: 'Arriba', icon: ArrowUp, gridArea: 'top' },
  { id: 'left', label: 'Izquierda', icon: ArrowLeft, gridArea: 'left' },
  { id: 'all', label: 'Todo', icon: Maximize2, gridArea: 'center' },
  { id: 'right', label: 'Derecha', icon: ArrowRight, gridArea: 'right' },
  { id: 'bottom', label: 'Abajo', icon: ArrowDown, gridArea: 'bottom' },
]

function DirectionSelector({
  value,
  onChange,
}: {
  value: Direction
  onChange: (d: Direction) => void
}) {
  return (
    <div
      className="grid gap-1 w-fit"
      style={{
        gridTemplateAreas: `
          ". top ."
          "left center right"
          ". bottom ."
        `,
        gridTemplateColumns: 'repeat(3, 2.5rem)',
        gridTemplateRows: 'repeat(3, 2.5rem)',
      }}
    >
      {DIRECTION_CONFIG.map(({ id, label, icon: Icon, gridArea }) => {
        const isActive = value === id
        return (
          <button
            key={id}
            type="button"
            title={label}
            onClick={() => onChange(id)}
            style={{ gridArea }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
              isActive
                ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                : 'bg-white border-slate-200 text-slate-500 hover:border-emerald-400 hover:text-emerald-600'
            }`}
          >
            <Icon className="w-4 h-4" />
          </button>
        )
      })}
    </div>
  )
}

function CanvasPreview({
  image,
  direction,
  expandPx,
}: {
  image: GeneratedImage | null
  direction: Direction
  expandPx: number
}) {
  if (!image) {
    return (
      <div className="flex items-center justify-center h-28 rounded-lg border border-dashed border-slate-200 text-slate-400 text-sm">
        Selecciona una imagen
      </div>
    )
  }

  const baseW = image.width ?? 1024
  const baseH = image.height ?? 1024

  const newW =
    direction === 'left' || direction === 'right' || direction === 'all'
      ? baseW + expandPx * (direction === 'all' ? 2 : 1)
      : baseW
  const newH =
    direction === 'top' || direction === 'bottom' || direction === 'all'
      ? baseH + expandPx * (direction === 'all' ? 2 : 1)
      : baseH

  const MAX = 200
  const scale = Math.min(MAX / newW, MAX / newH)
  const containerW = Math.round(newW * scale)
  const containerH = Math.round(newH * scale)
  const imgW = Math.round(baseW * scale)
  const imgH = Math.round(baseH * scale)

  const offsetX =
    direction === 'left'
      ? containerW - imgW
      : direction === 'all'
        ? Math.round((containerW - imgW) / 2)
        : 0
  const offsetY =
    direction === 'top'
      ? containerH - imgH
      : direction === 'all'
        ? Math.round((containerH - imgH) / 2)
        : 0

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative bg-slate-100 border border-slate-200 rounded overflow-hidden"
        style={{ width: containerW, height: containerH }}
      >
        {/* Checkerboard pattern for expanded area */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'repeating-conic-gradient(#cbd5e1 0% 25%, #e2e8f0 0% 50%)',
            backgroundSize: '12px 12px',
          }}
        />
        <img
          src={getImageAssetUrl(image.id)}
          alt="Preview"
          className="absolute object-cover"
          style={{ width: imgW, height: imgH, left: offsetX, top: offsetY }}
          onError={(e) => {
            if (image.public_url) {
              ;(e.target as HTMLImageElement).src = image.public_url
            }
          }}
        />
      </div>
      <p className="text-xs text-slate-500">
        {baseW}x{baseH} &rarr; {newW}x{newH}
      </p>
    </div>
  )
}

export function OutpaintingPanel({ images, onSuccess }: OutpaintingPanelProps) {
  const [selectedImageId, setSelectedImageId] = useState<string>(
    images[0]?.id ?? ''
  )
  const [direction, setDirection] = useState<Direction>('right')
  const [expandPx, setExpandPx] = useState(256)
  const [prompt, setPrompt] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const selectedImage = images.find((img) => img.id === selectedImageId) ?? null

  const handleSubmit = async () => {
    if (!selectedImageId) {
      toast.error('Selecciona una imagen para expandir')
      return
    }
    setIsSubmitting(true)
    try {
      await outpaintImage(selectedImageId, {
        direction,
        expand_px: expandPx,
        prompt: prompt.trim() || undefined,
      })
      toast.success('Expansión enviada a la cola. La nueva imagen aparecerá en la galería.')
      setPrompt('')
      onSuccess?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al expandir imagen')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (images.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
        No hay imágenes disponibles para expandir.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Image selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Imagen base
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setSelectedImageId(img.id)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                selectedImageId === img.id
                  ? 'border-emerald-500 ring-2 ring-emerald-200'
                  : 'border-transparent hover:border-slate-300'
              }`}
            >
              <img
                src={getImageAssetUrl(img.id)}
                alt="Imagen"
                className="w-full h-full object-cover"
                onError={(e) => {
                  if (img.public_url) {
                    ;(e.target as HTMLImageElement).src = img.public_url
                  }
                }}
              />
              {img.width && img.height && (
                <span className="absolute bottom-1 left-1 right-1 text-center text-[10px] text-white bg-black bg-opacity-50 rounded px-1">
                  {img.width}x{img.height}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-8">
        {/* Direction + Preview */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Dirección de expansión
            </label>
            <DirectionSelector value={direction} onChange={setDirection} />
            <p className="mt-2 text-xs text-slate-500">
              {DIRECTION_CONFIG.find((d) => d.id === direction)?.label}
              {direction === 'all' ? ' (todos los lados)' : ''}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Vista previa
            </label>
            <CanvasPreview
              image={selectedImage}
              direction={direction}
              expandPx={expandPx}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="flex-1 space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Expansión: <span className="text-emerald-600 font-semibold">{expandPx}px</span>
            </label>
            <input
              type="range"
              min={64}
              max={1024}
              step={64}
              value={expandPx}
              onChange={(e) => setExpandPx(Number(e.target.value))}
              className="w-full accent-emerald-600"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>64px</span>
              <span>1024px</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prompt de relleno{' '}
              <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe qué debe aparecer en el área expandida. Si se deja vacío, el modelo completará automáticamente."
              rows={4}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
            />
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedImageId}
            className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Maximize2 className="w-4 h-4" />
                Expandir canvas
              </>
            )}
          </button>

          {isSubmitting && (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <LoadingSpinner size="sm" />
              Procesando en segundo plano...
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
